import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ICONS } from "../shared/icons";
import { useSelector } from "react-redux";

import { selectTheme } from "../features/ui/uiSlice";
import { geocodeAddress } from "../shared/geocode";
import { BISHKEK_LNGLAT, boundsFromLatLngList, hasCoords, toLngLat } from "../shared/geo";
import { useYmaps } from "../shared/ymaps";
import YMapsGate from "./YMapsGate";
import "./StoreMap.css";

/**
 * Карта магазинов на Яндекс.Картах JS API 3.0 (императивный API — reactify рассчитан на React 18).
 */
function StoresCanvas({ ymaps3, stores, height, onStoreClick, theme, onMapReady }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const schemeRef = useRef(null);
  const markersRef = useRef([]);
  const onStoreClickRef = useRef(onStoreClick);
  onStoreClickRef.current = onStoreClick;

  // Пересчитываем маркеры только когда реально изменился состав или координаты,
  // а не на каждый рендер родителя (в leaflet-версии здесь был лишний ре-фит).
  const signature = useMemo(
    () => stores.map((s) => `${s.id}:${s.latitude}:${s.longitude}:${s.name}`).join("|"),
    [stores]
  );

  useEffect(() => {
    const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer } = ymaps3;

    const map = new YMap(containerRef.current, {
      location: { center: BISHKEK_LNGLAT, zoom: 12 },
    });
    const scheme = new YMapDefaultSchemeLayer({ theme });
    map.addChild(scheme);
    map.addChild(new YMapDefaultFeaturesLayer());

    mapRef.current = map;
    schemeRef.current = scheme;
    if (onMapReady) onMapReady(map);

    return () => {
      map.destroy();
      mapRef.current = null;
      schemeRef.current = null;
      markersRef.current = [];
      if (onMapReady) onMapReady(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ymaps3]);

  useEffect(() => {
    if (schemeRef.current) schemeRef.current.update({ theme });
  }, [theme]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((marker) => map.removeChild(marker));
    markersRef.current = [];

    const withCoords = stores.filter((s) => hasCoords(s.latitude, s.longitude));
    if (withCoords.length === 0) return;

    withCoords.forEach((store) => {
      const marker = new ymaps3.YMapMarker(
        { coordinates: toLngLat(store.latitude, store.longitude) },
        buildMarkerElement(store, () => onStoreClickRef.current)
      );
      map.addChild(marker);
      markersRef.current.push(marker);
    });

    // В v3 у setLocation({bounds}) нет padding — он заложен в boundsFromLatLngList.
    // Для одной точки bounds вырожден, поэтому центрируемся явным зумом.
    if (withCoords.length === 1) {
      const only = withCoords[0];
      map.setLocation({ center: toLngLat(only.latitude, only.longitude), zoom: 15, duration: 400 });
    } else {
      const bounds = boundsFromLatLngList(withCoords);
      if (bounds) map.setLocation({ bounds, duration: 400 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ymaps3, signature]);

  return <div ref={containerRef} className="store-map-canvas" style={{ height }} />;
}

/**
 * Маркер и его попап — обычный DOM: в JS API v3 нет Popup, как в leaflet.
 * Клики внутри гасим, иначе их перехватывает YMapListener карты.
 */
function buildMarkerElement(store, getOnStoreClick) {
  const root = document.createElement("div");
  root.className = "store-marker";

  const pin = document.createElement("div");
  pin.className = "store-marker-pin";
  root.appendChild(pin);

  const popup = document.createElement("div");
  popup.className = "store-popup";
  popup.hidden = true;

  const name = document.createElement("div");
  name.className = "store-popup-name";
  name.textContent = store.name || "—";
  popup.appendChild(name);

  const address = document.createElement("div");
  address.className = "store-popup-address";
  address.textContent = store.address || "—";
  popup.appendChild(address);

  const onStoreClick = getOnStoreClick();
  if (onStoreClick) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "store-popup-btn";
    button.textContent = "Подробнее →";
    button.addEventListener("click", (e) => {
      e.stopPropagation();
      const handler = getOnStoreClick();
      if (handler) handler(store);
    });
    popup.appendChild(button);
  }

  popup.addEventListener("click", (e) => e.stopPropagation());
  root.appendChild(popup);

  pin.addEventListener("click", (e) => {
    e.stopPropagation();
    // Открыт максимум один попап — как было у leaflet.
    document.querySelectorAll(".store-popup:not([hidden])").forEach((el) => {
      if (el !== popup) el.hidden = true;
    });
    popup.hidden = !popup.hidden;
  });

  return root;
}

/**
 * StoreMap — карта всех магазинов с поиском адреса.
 *
 * Props:
 *   stores       — массив { id, name, address, latitude, longitude }
 *   height       — CSS-высота, default '480px'
 *   onStoreClick — необязательный callback(store) из попапа маркера
 */
export default function StoreMap({ stores = [], height = "480px", onStoreClick }) {
  const { status, ymaps3, error } = useYmaps();
  const theme = useSelector(selectTheme);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const mapRef = useRef(null);

  const storesWithCoords = stores.filter((s) => hasCoords(s.latitude, s.longitude));

  const handleSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearchStatus("searching");
    try {
      const found = await geocodeAddress(q);
      if (!found) {
        setSearchStatus("not_found");
        return;
      }
      if (mapRef.current) {
        mapRef.current.setLocation({
          center: toLngLat(found.lat, found.lng),
          zoom: 15,
          duration: 600,
        });
      }
      setSearchStatus("ok");
    } catch {
      setSearchStatus("error");
    }
  }, [searchQuery]);

  return (
    <div className="store-map-container">
      <div className="store-map-toolbar">
        <input
          className="store-map-search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setSearchStatus("");
          }}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Поиск на карте (адрес, город…)"
        />
        <button
          className="store-map-btn"
          onClick={handleSearch}
          title="Найти на карте"
          aria-label="Найти на карте"
        >
          {ICONS.search}
        </button>
        {searchStatus === "not_found" && <span className="store-map-msg">Адрес не найден</span>}
        {searchStatus === "error" && <span className="store-map-msg store-map-msg--error">Ошибка запроса</span>}
        <span className="store-map-count">
          {storesWithCoords.length} из {stores.length} магазинов на карте
        </span>
      </div>

      <YMapsGate status={status} error={error} height={height}>
        <StoresCanvas
          ymaps3={ymaps3}
          stores={stores}
          height={height}
          onStoreClick={onStoreClick}
          theme={theme}
          onMapReady={(map) => {
            mapRef.current = map;
          }}
        />
      </YMapsGate>

      {storesWithCoords.length === 0 && (
        <div className="store-map-empty">Ни у одного магазина нет координат</div>
      )}
    </div>
  );
}

function SingleStoreCanvas({ ymaps3, latitude, longitude, name, height, theme }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapMarker } = ymaps3;
    const coordinates = toLngLat(latitude, longitude);

    const map = new YMap(containerRef.current, { location: { center: coordinates, zoom: 15 } });
    map.addChild(new YMapDefaultSchemeLayer({ theme }));
    map.addChild(new YMapDefaultFeaturesLayer());

    const root = document.createElement("div");
    root.className = "store-marker";
    const pin = document.createElement("div");
    pin.className = "store-marker-pin";
    root.appendChild(pin);
    if (name) {
      const label = document.createElement("div");
      label.className = "store-marker-label";
      label.textContent = name;
      root.appendChild(label);
    }
    map.addChild(new YMapMarker({ coordinates }, root));

    return () => map.destroy();
  }, [ymaps3, latitude, longitude, name, theme]);

  return <div ref={containerRef} className="store-map-canvas" style={{ height }} />;
}

/**
 * SingleStoreMap — карта одного магазина, только для чтения.
 */
export function SingleStoreMap({ latitude, longitude, name, height = "280px" }) {
  // Хуки — до любого раннего return.
  const { status, ymaps3, error } = useYmaps();
  const theme = useSelector(selectTheme);

  if (!hasCoords(latitude, longitude)) {
    return <div className="store-map-no-coords">Координаты не указаны</div>;
  }

  return (
    <YMapsGate status={status} error={error} height={height}>
      <SingleStoreCanvas
        ymaps3={ymaps3}
        latitude={latitude}
        longitude={longitude}
        name={name}
        height={height}
        theme={theme}
      />
    </YMapsGate>
  );
}
